import { ApplyVetterPricesShippingSource, type OrderShippingMethod } from '@codezo/graphql';
import { cn } from '../../../lib/utils';
import { CurrencyText } from '../../CurrencyText';
import { Input } from '../../ui/input';
import { AffixInput } from './AffixInput';
import type { QuantityPreview } from './ApplyPricesProductTable';

export type ShippingLinePreview = QuantityPreview['shipping'][number];

/** A delivery price the studio typed for one line. Only touched lines are sent. */
export interface ShipDraft {
	price: string;
	daysMin: string;
	daysMax: string;
	/** Carried for a `new:` slot, which is sent by method rather than id. */
	shippingMethod: OrderShippingMethod;
}

const num = (n: number | null | undefined) =>
	n == null ? '' : n.toLocaleString('en-US', { maximumFractionDigits: 2 });
const digits = (v: string) => v.replace(/\D/g, '');

/**
 * Every delivery line of one quantity, each with a price box and a days range.
 * The boxes show what Apply would put there (the supplier's quote, or what the
 * offer has) and typing overrides it; an empty box keeps that. A Standard or
 * Express slot the quantity doesn't have yet is only created once it has a price.
 */
export function ApplyShippingLines({
	lines,
	drafts,
	onChange,
	currency,
	stale,
}: {
	lines: ShippingLinePreview[];
	/** Typed values per line id. */
	drafts: Record<string, ShipDraft>;
	onChange: (lineId: string, next: ShipDraft) => void;
	currency: string;
	stale: boolean;
}) {
	return (
		<div className="flex flex-col gap-1.5">
			<span className="text-2xs tracking-[0.06em] text-text-4 uppercase">Shipping</span>
			{lines.map((line) => {
				const draft = drafts[line.id] ?? {
					price: '',
					daysMin: '',
					daysMax: '',
					shippingMethod: line.shippingMethod,
				};
				const set = (patch: Partial<ShipDraft>) =>
					onChange(line.id, { ...draft, ...patch });
				const typed = draft.price.trim() !== '';
				return (
					<div
						key={line.id}
						className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2"
					>
						<span className="flex min-w-0 items-baseline gap-2 text-xs">
							<span className="truncate text-text-2" title={line.label}>
								{line.label}
							</span>
							<span className={cn('shrink-0 text-text-4', stale && 'opacity-50')}>
								<LineNote line={line} typed={typed} />
							</span>
						</span>
						<AffixInput
							value={draft.price}
							onChange={(price) => set({ price })}
							placeholder={num(line.after.price) || '–'}
							suffix={currency}
							label={`${line.label} price`}
						/>
						<div className="flex items-center gap-1">
							<Input
								inputMode="numeric"
								value={draft.daysMin}
								onChange={(e) => set({ daysMin: digits(e.target.value) })}
								placeholder={num(line.after.deliveryDaysMin) || 'min'}
								aria-label={`${line.label} delivery days, from`}
								className="h-8 w-12 text-center font-mono text-sm"
							/>
							<span className="text-text-4">–</span>
							<Input
								inputMode="numeric"
								value={draft.daysMax}
								onChange={(e) => set({ daysMax: digits(e.target.value) })}
								placeholder={num(line.after.deliveryDaysMax) || 'max'}
								aria-label={`${line.label} delivery days, to`}
								className="h-8 w-12 text-center font-mono text-sm"
							/>
							<span className="text-xs text-text-4">d</span>
						</div>
					</div>
				);
			})}
		</div>
	);
}

/** Where the line's price comes from, quietly: the supplier, a new option, or what it was. */
function LineNote({ line, typed }: { line: ShippingLinePreview; typed: boolean }) {
	if (line.createsOption) return <>{line.after.price != null ? 'new option' : 'not offered'}</>;
	if (typed || line.source === ApplyVetterPricesShippingSource.Typed) {
		return line.before?.price != null ? (
			<>
				was <CurrencyText amount={line.before.price} />
			</>
		) : null;
	}
	if (line.source === ApplyVetterPricesShippingSource.Supplier) {
		return line.changed && line.before?.price != null ? (
			<>
				supplier&apos;s · was <CurrencyText amount={line.before.price} />
			</>
		) : (
			<>supplier&apos;s</>
		);
	}
	return null;
}
